import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ApprovalState, Client, ReportType, RiptTabDto } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { LoaderService } from '@fdm/services';
import { getErrorMessage, noWhitespaceValidator } from '@fdm/utils';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  filter,
  first,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { FormValidationService, NavigationService, ReportFormPage, ReportsBulkSaveService, ReportTask } from '../../../../services';
import { HttpClient } from '@angular/common/http';
import { ReportForm } from '../../report-form';

interface RiptApiResponse {
  SeriatimRank: number;
  ABCPrioritization: string;
  Criteria: string;
  HSERiskEfficiency: number;
  ROR: number;
  DPI: number;
  InitialInvestment: number;
  LikelyScenario: string | null;
  LikelyConsequence: string | null;
  RiskScore: string;
  RiskCategory: string;
}

@Component({
  selector: 'app-ript-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,

    // Material
    MaterialModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    MatSlideToggle,
  ],
  templateUrl: './ript-tab.component.html',
  styleUrls: ['./ript-tab.component.scss'],
})
export class RiptTabComponent extends ReportForm implements OnInit {
  private _fb = inject(FormBuilder);
  private _router = inject(Router);
  private _navigationService = inject(NavigationService);
  private _route = inject(ActivatedRoute);
  private _loader = inject(LoaderService);
  private _apiClient = inject(Client);
  private _destroyRef = inject(DestroyRef);
  private _toastrService = inject(ToastrService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private _formValidationService = inject(FormValidationService);

  private _http = inject(HttpClient);

  public form: FormGroup = this._fb.group({
    id: [''],
    riptUrl: ['', [Validators.maxLength(2000)]],
    criteriaDrivingScore: ['', [Validators.maxLength(2000)]],
    abcPrioritization: ['', [Validators.maxLength(2000)]],
    seriatimRank: [null, [Validators.min(-2000000000), Validators.max(2000000000)]],
    initialInvestment: [null, [Validators.min(-100000000000), Validators.max(100000000000)]],
    hseRiskEfficiency: [null, [Validators.min(-100000000000), Validators.max(100000000000)]],
    needRiskAnalysis: [false],
    likelyScenario: ['', [Validators.maxLength(20000)]],
    likelyConsequence: ['', [Validators.maxLength(20000)]],
    riskManRiskScore: ['', [Validators.maxLength(100)]],
    riskManCategory: ['', [Validators.maxLength(100)]],
    rorPercent: [null, [Validators.min(-100000000000), Validators.max(100000000000)]],
    dpi: [null, [Validators.min(-100000000000), Validators.max(100000000000)]],
    assetRisk: ['', [Validators.maxLength(3)]],
    riskConsiderationId: [0],
    riskConsideration: this._fb.group({
      id: [0],
      roKConsiderationsEN: ['', [Validators.maxLength(20000)]],
      roKConsiderationsRU: ['', [Validators.maxLength(20000)]],
      roKConsiderationsKZ: ['', [Validators.maxLength(20000)]],
    }),
    technicalAssesmentId: [0],
    technicalAssesment: this._fb.group({
      id: [0],
      operatingConditionDescriptionEN: ['', [Validators.maxLength(20000)]],
      operatingConditionDescriptionRU: ['', [Validators.maxLength(20000)]],
      operatingConditionDescriptionKZ: ['', [Validators.maxLength(20000)]],
      technicalAssesmentDescriptionEN: ['', [Validators.maxLength(20000)]],
      technicalAssesmentDescriptionRU: ['', [Validators.maxLength(20000)]],
      technicalAssesmentDescriptionKZ: ['', [Validators.maxLength(20000)]],
    }),
  });

  public override readonlyFields: string[] = [
    'criteriaDrivingScore',
    'abcPrioritization',
    'seriatimRank',
    'initialInvestment',
    'hseRiskEfficiency',
    'likelyScenario',
    'likelyConsequence',
    'riskManRiskScore',
    'riskManCategory',
    'rorPercent',
    'dpi',
    'assetRisk',
  ];

  protected displayNames: { [key: string]: string } = {
    riptUrl: 'RIPT URL',
    criteriaDrivingScore: 'Criteria driving Score',
    abcPrioritization: 'RIPT prioritization',
    seriatimRank: 'Seriatim Rank',
    initialInvestment: 'Initial investment',
    hseRiskEfficiency: 'HSE Risk Efficiency',
    needRiskAnalysis: 'Need Risk Analysis?',
    likelyScenario: 'Likely Scenario',
    likelyConsequence: 'Likely Consequence',
    riskManRiskScore: 'RiskMan Risk Score',
    riskManCategory: 'RiskMan Category',
    rorPercent: 'ROR (%)',
    dpi: 'DPI',
    assetRisk: 'Asset Risk',
    'technicalAssesment.operatingConditionDescriptionEN': 'Operating condition description [EN]',
    'technicalAssesment.operatingConditionDescriptionRU': 'Operating condition description [RU]',
    'technicalAssesment.operatingConditionDescriptionKZ': 'Operating condition description [KZ]',
    'technicalAssesment.technicalAssesmentDescriptionEN': 'Technical assessment description [EN]',
    'technicalAssesment.technicalAssesmentDescriptionRU': 'Technical assessment description [RU]',
    'technicalAssesment.technicalAssesmentDescriptionKZ': 'Technical assessment description [KZ]',
    'riskConsideration.roKConsiderationsEN': 'RoK Considerations [EN]',
    'riskConsideration.roKConsiderationsRU': 'RoK Considerations [RU]',
    'riskConsideration.roKConsiderationsKZ': 'RoK Considerations [KZ]',
  };

  public get needRiskAnalysis() {
    return this.form.get('needRiskAnalysis') as FormControl<boolean>;
  }

  public get abcPrioritization() {
    return this.form.get('abcPrioritization') as FormControl<string>;
  }

  public get operatingConditionDescriptionEN() {
    return this.form.get('technicalAssesment.operatingConditionDescriptionEN') as FormControl<string | null>;
  }

  public get technicalAssesmentDescriptionEN() {
    return this.form.get('technicalAssesment.technicalAssesmentDescriptionEN') as FormControl<string | null>;
  }

  public get roKConsiderationsEN() {
    return this.form.get('riskConsideration.roKConsiderationsEN') as FormControl<string | null>;
  }

  public get riptUrl() {
    return this.form.get('riptUrl') as FormControl<string | null>;
  }

  private _generalTabId: string | null = null;
  private _closeReport = toSignal(this._store.closeReport$);
  private currentStep = toSignal(this._store.page$);
  private _general = toSignal(this._store.general$);

  public get step() {
    return ReportFormPage.RIPT;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this._initTriggers();
    this._watchRiptUrlChanges();

    if (this._store.reportType() !== ReportType.TaskDeferral) {
      this._formValidationService.registerForm(ReportFormPage.RIPT, this.form);
    }
  }
  private _watchRiptUrlChanges(): void {
    this.riptUrl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        // If riptUrl is cleared, clear all RIPT fields
        if (!value || value.trim() === '') {
          this.form.patchValue({
            seriatimRank: null,
            abcPrioritization: '',
            criteriaDrivingScore: '',
            hseRiskEfficiency: null,
            rorPercent: null,
            dpi: null,
            initialInvestment: null,
            likelyScenario: '',
            likelyConsequence: '',
            riskManRiskScore: '',
            riskManCategory: '',
          }, { emitEvent: false });
        }
      });
  }

  public getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(this.form, fieldName, this.displayNames);
  }

  getRiptData(riptUrl: string): void {

    if (riptUrl) {
      this._loader.startProcessing();
      this._apiClient.riptData(riptUrl).subscribe(
        (response: any[]) => {
          if (response) {
            this.form.patchValue({
              seriatimRank: response[0].IM_Seriatim_Rank__c,
              abcPrioritization: response[0].IM_ABC_Prioritization__c,
              criteriaDrivingScore: response[0].IM_Criteria__c,
              hseRiskEfficiency: response[0].IM_HSE_Risk_Efficiency__c,
              rorPercent: response[0].IM_ROR__c,
              dpi: response[0].IM_DPI__c,
              initialInvestment: response[0].IM_Initial_Investment__c,
              likelyScenario: response[0].IM_RIPT_Likely_Scenario__c,
              likelyConsequence: response[0].IM_RIPT_Likely_Consequence__c,
              riskManRiskScore: response[0].IM_RiskMan_Risk_Score__c,
              riskManCategory: response[0].IM_RiskMan_Category__c,
            });
          }
          this._loader.stopProcessing();
        },
        (error: any) => {
          this._loader.stopProcessing();
          this._toastrService.error('Failed to fetch RIPT data. Please check the RIPT URL and try again.');
        }
      );
    } else {
      this._toastrService.error('Invalid RIPT URL format. Please check and try again.');
    }
  }

  private _initTriggers(): void {
    this._route.paramMap
      .pipe(
        filter(() => !this.form.dirty),
        switchMap((params) => {
          this._generalTabId = params.get('id') ?? null;

          return this._getRipt();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();

    this.needRiskAnalysis?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (!value) {
          this.operatingConditionDescriptionEN.setValidators(null);
          this.technicalAssesmentDescriptionEN.setValidators(null);
          this.roKConsiderationsEN.setValidators(null);
          this.operatingConditionDescriptionEN.addValidators(Validators.maxLength(20000));
          this.technicalAssesmentDescriptionEN.addValidators(Validators.maxLength(20000));
          this.roKConsiderationsEN.addValidators(Validators.maxLength(20000));

          this.form.patchValue({
            operatingConditionDescriptionEN: '',
            operatingConditionDescriptionRU: '',
            operatingConditionDescriptionKZ: '',
            technicalAssesmentDescriptionEN: '',
            technicalAssesmentDescriptionRU: '',
            technicalAssesmentDescriptionKZ: '',
            roKConsiderationsEN: '',
            roKConsiderationsRU: '',
            roKConsiderationsKZ: '',
          });
        } else {
          this.operatingConditionDescriptionEN.addValidators([Validators.required, Validators.maxLength(20000), noWhitespaceValidator()]);
          this.technicalAssesmentDescriptionEN.addValidators([Validators.required, Validators.maxLength(20000), noWhitespaceValidator()]);
          this.roKConsiderationsEN.addValidators([Validators.required, Validators.maxLength(20000), noWhitespaceValidator()]);
        }

        this.operatingConditionDescriptionEN.updateValueAndValidity();
        this.technicalAssesmentDescriptionEN.updateValueAndValidity();
        this.roKConsiderationsEN.updateValueAndValidity();
      });

    this.abcPrioritization.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(value => {
        this._store.setAbcPrioritization(value);
      });

    this._store.page$
      .pipe(
        filter((page) => page === this.step),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this._store.setPageValidity({ page: this.step, isStepValid: this.form.valid }));

    this.form.valueChanges
      .pipe(
        filter(() => this.step === this.currentStep()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        const value = this.form.getRawValue();
        const isFormValid = this.form.valid;

        const payload = RiptTabDto.fromJS({
          ...value,
          id: value.id ? value.id : undefined,
          riskConsiderationId: value.riskConsiderationId ? value.riskConsiderationId : undefined,
          riskConsideration: value.needRiskAnalysis ? value.riskConsideration : undefined,
          technicalAssesmentId: value.technicalAssesmentId ? value.technicalAssesmentId : undefined,
          technicalAssesment: value.needRiskAnalysis ? value.technicalAssesment : undefined,
          generalTabId: this._generalTabId,
        });

        this._store.setRipt(payload);

        if (isFormValid) {
          const payload: ReportTask = {
            page: this.step,
            request: this._save()
          };

          if (this._bulkSave.includes(this.step)) {
            this._bulkSave.update(payload);
          } else {
            this._bulkSave.add(payload);
          }
        }

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid })
      });

    this.riptUrl.valueChanges
      .pipe(
        filter((value) => !!value?.includes('view')),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((value) => {
        if (value) {
          this.getRiptData(value);
        }
      });

      // TODO: need to ignore for recycle, out-of-service & cancelled
      this._store.general$
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(general => {
          if (general?.currentState === ApprovalState.PlanConfirmed && (general.reportType === ReportType.IntegrityThreat || general.reportType === ReportType.ReliabilityThreat)) {
            this.riptUrl.addValidators([Validators.required, Validators.maxLength(2000)]);
          } else {
            this.riptUrl.setValidators(null);
          }
          this.riptUrl.updateValueAndValidity({ emitEvent: false });
        });
  }

  private _save() {
    const values = this.form.getRawValue();
    const needRiskAnalysis = values.needRiskAnalysis;

    const payload = RiptTabDto.fromJS({
      ...values,
      id: values.id ? values.id : undefined,
      riskConsiderationId: values.riskConsiderationId ? values.riskConsiderationId : undefined,
      riskConsideration: needRiskAnalysis ? values.riskConsideration : undefined,
      technicalAssesmentId: values.technicalAssesmentId ? values.technicalAssesmentId : undefined,
      technicalAssesment: needRiskAnalysis ? values.technicalAssesment : undefined,
      generalTabId: this._generalTabId,
    });

    // this._loader.startProcessing();

    return this._handleSave(payload)
      .pipe(
        switchMap(() => {
          this._store.reset();

          if (this._closeReport()) {
            this._navigationService.navigateToListPage();
            return of(null);
          }

          return this._getRipt();
        }),
        catchError((err) => {
          console.warn('err', err);
          this._toastrService.error(`${err.message}` || 'An error occurred while saving the data.'); //temp Solution
          this._store.reset();
          return of(err);
        }),
        first()
      )
  }

  private _handleSave(payload: RiptTabDto) {
    return payload.id
      ? this._apiClient.riptTabPUT(payload.id, payload).pipe(
        map(() => payload.id),
        tap(() => {
          // hide notification for closing
          if (this._general()?.currentState !== ApprovalState.ValidationAndVerificationCompleted) {
            this._toastrService.success('RIPT updated successfully!');
          }
          this._store.setRipt(payload);
        })
      )
      : this._apiClient.riptTabPOST(payload)
        .pipe(
          tap(() => {
            this._toastrService.success('RIPT saved successfully!');
            this._store.setRipt(payload);
          })
        );
  }

  private _getRipt() {
    return this._generalTabId
      ? this._apiClient
        .byGeneralTab5(this._generalTabId)
        .pipe(
          tap((data) => {
            this.form.patchValue(data, { emitEvent: false });
            this._store.setRipt(data);
          }),
          catchError(err => {
            return of(err);
          })
        )
      : of(null);
  }
}
